
import React, { useState, useMemo, useEffect } from "react";
import "banner-pattern/dist/main.css";
import { FieldConfig } from "../../../types/formTypes";
import StepIndicator, {
  StepStatus,
  StepIndicatorItem,
} from "../../StepIndicator/StepIndicator";
import { tuitionSteps } from "../../../config/steps/tuitionStepsConfig";
import BreadCrumbs from "../BreadCrumbs/BreadCrumbs";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearForm } from "../../../store/slices/Citizenship/citizenshipFormSlice";
import plusOutline from "../../../../public/assets/images/plus-outline.svg";
import trashSolid from "../../../../public/assets/images/trash-solid.svg";
import { TuitionFormData } from "../../../config/tuitionFormConfig";
import TuitionErrorBanner from "../TuitionErrorBanner";
import UploadErrorBanner from "../../../pages/CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner";
import { safeGet } from "../../../utils/safeGet";
import ScrollToTopBtn from "../ScrollToTopBtn";

type FormMode = 'employee' | 'admin';

interface StepFields {
  step1: FieldConfig[];
  step2: FieldConfig[];
  step3: FieldConfig[];
  step4?: FieldConfig[];
  step5?: FieldConfig[];
  step6?: FieldConfig[];
  step7?: FieldConfig[];
  step8?: FieldConfig[];
}

interface StepTitles {
  step1: string;
  step2: string;
  step3: string;
  step4?: string;
  step5?: string;
  step6?: string;
  step7?: string;
  step8?: string;
}

interface FormStepperProps {
  stepFields: StepFields;
  stepTitles: StepTitles;
  totalSteps: number;
  renderForm: (
    stepFields: FieldConfig[],
    stepTitle: string,
    buttons: string[],
    onNext?: () => void,
    onBack?: () => void,
    onClear?: () => void,
    currentStep?: number
  ) => React.ReactNode;
  showSuccess?: boolean;
  successContent?: React.ReactNode;
  stepLabels?: string[];
  commentsBanner?: React.ReactNode;
  warningBanner?:React.ReactNode;
  adminEmpIdErrorBanner?: React.ReactNode;
  patchErrorBanner?: React.ReactNode;
  updateManagerDetailsErrorBanner?: React.ReactNode;
  formValues?: TuitionFormData;
  validationErrors?: Record<string, string>;
  onStepChange?: () => void;
  setFormValues?: React.Dispatch<React.SetStateAction<TuitionFormData>>;
  onClearValidationErrors?: () => void;
  onUpdateValidationErrors?: (errors: Record<string, string>) => void;
  mode?:FormMode;
  initialStep?: number | null;
  hideBreadcrumbs?: boolean;
  showErrorCounts?: boolean; // Show error counts on step indicator (error correction mode)
  isErrorCorrectionMode?: boolean; // Flag to indicate error correction flow (disables steps 1-3)
}

const FormStepperTuition: React.FC<FormStepperProps> = ({
  stepFields,
  stepTitles,
  totalSteps,
  renderForm,
  showSuccess = false,
  successContent,
  stepLabels,
  commentsBanner,
  warningBanner,
  adminEmpIdErrorBanner,
  patchErrorBanner,
  updateManagerDetailsErrorBanner,
  formValues,
  validationErrors = {},
  onStepChange,
  setFormValues,
  onClearValidationErrors,
  onUpdateValidationErrors,
 mode = 'employee',
 initialStep = null,
 hideBreadcrumbs = false,
 showErrorCounts = false,
 isErrorCorrectionMode = false
}) => {

  const [currentStep, setCurrentStep] = useState<number>(initialStep || 1);
  const [classCount, setClassCount] = useState<number>(1);
  const [classIds, setClassIds] = useState<string[]>([`${Date.now()}-0`]);
  // Sync classCount and classIds with formValues.classCount in error correction mode
  useEffect(() => {
    if (isErrorCorrectionMode && formValues) {
      let newCount = typeof (formValues as any).classCount === 'number'
        ? Number((formValues as any).classCount)
        : 1;
      // Detect the max class index from initialValues keys (e.g., courseName_class1)
      const classIndices = Object.keys(formValues)
        .map(key => {
          const match = /_class(\d+)/.exec(key);
          return match ? Number(match[1]) : null;
        })
        .filter(idx => idx !== null);
      const detectedCount = classIndices.length > 0 ? Math.max(...classIndices) + 1 : newCount;
      // If detectedCount is greater than newCount, use detectedCount
      if (detectedCount > newCount) {
        newCount = detectedCount;
      }
      if (newCount !== classCount) {
        setClassCount(newCount);
        setClassIds(Array.from({ length: newCount }, (_, i) => `${Date.now()}-${i}`));
      }
    }
  }, [isErrorCorrectionMode, formValues, classCount]);
  const [resetKeys, setResetKeys] = useState<Record<number, number>>({});
  const [initialValues, setInitialValues] = useState<TuitionFormData | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdminCreate = mode === 'admin';
  const errorBannerRef= React.useRef<HTMLDivElement>(null);
  // Update currentStep when initialStep prop changes (for error correction navigation)
  useEffect(() => {
    if (initialStep !== null && initialStep !== currentStep) {
      setCurrentStep(initialStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStep]);

 

  // In error correction mode, only show steps 1-5
  const steps = useMemo(() => {
    if (isErrorCorrectionMode) {
      // Only show first 5 steps, update step 5 label
      const baseSteps = (stepLabels || tuitionSteps).slice(0, 5);
      if (baseSteps.length === 5) {
        baseSteps[4] = 'Class information and Submit';
      }
      return baseSteps;
    }
    return stepLabels || tuitionSteps;
  }, [isErrorCorrectionMode, stepLabels, tuitionSteps]);

  // Field name groups used to split class fields
  const travelFieldNames = useMemo(
    () =>
      new Set([
        "estimatedAirfareBaggageFee",
        "estimatedAutoRentalFee",
        "estimatedParkTollFee",
        "estimatedPublicTransportFee",
        "estimatedHotelFee",
      ]),
    []
  );

  const milesFieldNames = useMemo(
    () =>
      new Set([
        "totalNumberOfMiles",
        "estimatedMilesDollarValue",
        "originAddress",
        "originCity",
        "originState",
        "originZipCode",
        "destinationCity",
        "destinationState",
        "destinationZipCode",
        "destinationAddress",
        "numberOfTravelDays",
        "milesTotalEstimatedCourseFees",
        "milesSchoolName",
      ]),
    []
  );


  useEffect(() => {
    if (!initialValues && formValues) {
      setInitialValues(formValues);
    }
  }, [formValues, initialValues]);

  // Scroll to TuitionErrorBanner when validation errors appear
  React.useEffect(() => {
    if (Object.keys(validationErrors).length > 0 && errorBannerRef.current) {
      errorBannerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [validationErrors]);
  // Helper to apply locked/read-only properties to a field config
const makeLockedField = (field: FieldConfig): FieldConfig => {
  return {
    ...field,
    disabled: true,
    helperText: "Read only; this value is assigned automatically by HR",
    required: false,
    type: field.type === "select" ? "text" : field.type,
    placeholder:  "",
    // Add options if this is a select field and options exist
    ...(field.type === "select" && field.options ? { options: field.options } : {}),
  };
}
  // Helper function to get fields for a specific step
  // --- Step field helpers to reduce complexity ---
  const getStep1Fields = () => {
    const base = stepFields.step1 || [];
    if (isErrorCorrectionMode) return base.map(makeLockedField);
    if (!isAdminCreate) return base;
    return base.map((f) =>
      f.name === 'empId' ? { ...f, disabled: false, helperText: undefined, required: true } : f
    );
  };

  const getStep2Fields = () => {
    const base = stepFields.step2 ?? [];
    if (isErrorCorrectionMode) return base.map(makeLockedField);
    return base;
  };

  const getStep3Fields = () => {
    const base = stepFields.step3 || [];
    if (isErrorCorrectionMode) return base.map(makeLockedField);
    if (!isAdminCreate) return base;
    return base.filter((field) => field.name !== 'alternateNuid');
  };

const filterOutApplicationYear = (fields: FieldConfig[]) =>
  fields.filter(f => f.name !== 'applicationYear');

const getStep4Fields = () => {
  const base = stepFields.step4 ?? [];
  if (isErrorCorrectionMode && mode === 'admin') {
    const errored = new Set(Object.keys(validationErrors));
    return filterOutApplicationYear(base).map(f =>
      errored.has(f.name) ? { ...f, disabled: false } : makeLockedField(f)
    );
  }
  if (isErrorCorrectionMode) {
    return filterOutApplicationYear(base);
  }
  if (isAdminCreate) {
    return base.map(f =>
      f.name === 'applicationYear' ? { ...f, required: true } : f
    );
  }
  return filterOutApplicationYear(base);
};



  const getStep5Fields = () => {
    const base = stepFields.step5 ?? [];
    if (isErrorCorrectionMode && mode === 'admin') {
      const errored = new Set(Object.keys(validationErrors));
      return base.map(f => {
        return { ...f, disabled: !errored.has(f.name) };
      });
    }
    if (isErrorCorrectionMode) {
      return base;
    }
    return base;
  };

  const getFieldsForStep = (step: number): FieldConfig[] => {
    switch (step) {
      case 1:
        return getStep1Fields();
      case 2:
        return getStep2Fields();
      case 3:
        return getStep3Fields();
      case 4:
        return getStep4Fields();
      case 5:
        return getStep5Fields();
      case 6:
        return stepFields.step6 ?? [];
      case 7:
        return stepFields.step7 ?? [];
      case 8:
        return stepFields.step8 ?? [];
      default:
        return [];
    }
  };

  // Calculate error count per step (only when showErrorCounts is true)
  const getErrorCountForStep = (step: number): number => {
    // Only calculate error counts when explicitly enabled (error correction mode)
    if (!showErrorCounts) return 0;

    // In error correction mode step 5, count all errors for class fields
    if (isErrorCorrectionMode && step === 5) {
      // Count all errors for fields matching step5 base fields and _classN fields
      const baseNames = stepFields.step5?.map(f => f.name) ?? [];
      return Object.keys(validationErrors).filter(fieldName => {
        if (fieldName === 'applicationYear') return false;
        // Match base fields or _classN fields
        if (baseNames.some(name => fieldName === name || fieldName.startsWith(name + '_class'))) {
          return true;
        }
        return false;
      }).length;
    }

    const stepFieldNamesSet = new Set(getFieldsForStep(step).map(f => f.name));
    return Object.keys(validationErrors).filter(fieldName => {
      // In error correction mode, exclude applicationYear from error counts
      if (isErrorCorrectionMode && fieldName === 'applicationYear') {
        return false;
      }
      return stepFieldNamesSet.has(fieldName);
    }).length;
  };

  const items: StepIndicatorItem[] = useMemo(() => {
    return steps.map((label, idx) => {
      const stepNum = idx + 1;
      let status: StepStatus = "inactive";
      if (stepNum < currentStep) status = "complete";
      else if (stepNum === currentStep) status = "active";
      
      // Only include error count when showErrorCounts is enabled AND there are actual errors
      const errorCount = showErrorCounts ? getErrorCountForStep(stepNum) : undefined;
      
      return { 
        label, 
        status, 
        ...(errorCount && errorCount > 0 ? { errorCount } : {}) // Only include errorCount if > 0
      };
    });
  }, [currentStep, steps, validationErrors, showErrorCounts]);

  // --- Per-step helpers for getCurrentStepFields ---
  const getCurrentStep1Fields = () => {
    const base = stepFields.step1 || [];
    if (isErrorCorrectionMode) {
      const disabledFields = base.map(f => ({ ...f, disabled: true, required: false }));
      return disabledFields;
    }
    if (!isAdminCreate) return base;
    return base.map((f) =>
      f.name === 'empId' ? { ...f, disabled: false, helperText: undefined, required: true } : f
    );
  };

  const getCurrentStep2Fields = () => {
    const base = stepFields.step2 ?? [];
    if (isErrorCorrectionMode) {
      return base.map(f => ({ ...f, disabled: true }));
    }
    return base;
  };

  const getCurrentStep3Fields = () => {
    const base = stepFields.step3 || [];
    if (isErrorCorrectionMode) {
      return base.map(f => ({ ...f, disabled: true }));
    }
    if (!isAdminCreate) return base;
    return base.filter((field) => field.name !== 'alternateNuid');
  };

  const getCurrentStep4Fields = () => {
    const base = stepFields.step4 ?? [];
    if (isErrorCorrectionMode && mode === 'admin') {
      const errored = new Set(Object.keys(validationErrors));
      return base
        .filter(f => f.name !== 'applicationYear')
        .map(f => errored.has(f.name) ? { ...f, disabled: false } : makeLockedField(f));
    }
    if (isErrorCorrectionMode) {
      return base.filter(f => f.name !== 'applicationYear');
    }
    if (isAdminCreate) {
      return base.map(f =>
        f.name === 'applicationYear' ? { ...f, required: true } : f
      );
    } else {
      return base.filter(f => f.name !== 'applicationYear');
    }
  };



  const getCurrentStep5Fields = () => {
    const base = stepFields.step5 ?? [];
    if (isErrorCorrectionMode && mode === 'admin') {
      const errored = new Set(Object.keys(validationErrors));
      return base.map(f => {
        return errored.has(f.name) ? { ...f, disabled: false } : makeLockedField(f);
      });
    }
    if (isErrorCorrectionMode) {
      return base;
    }
    return base;
  };

  const getCurrentStepFields = (): FieldConfig[] => {
    switch (currentStep) {
      case 1:
        return getCurrentStep1Fields();
      case 2:
        return getCurrentStep2Fields();
      case 3:
        return getCurrentStep3Fields();
      case 4:
        return getCurrentStep4Fields();
      case 5:
        return getCurrentStep5Fields();
      case 6:
        return stepFields.step6 ?? [];
      case 7:
        return stepFields.step7 ?? [];
      case 8:
        return stepFields.step8 ?? [];
      default:
        return [];
    }
  };

  const getCurrentStepTitle = (): string => {
    switch (currentStep) {
      case 1:
        return stepTitles.step1 ?? "";
      case 2:
        return stepTitles.step2 ?? "";
      case 3:
        return stepTitles.step3 ?? "";
      case 4:
        return stepTitles.step4 ?? "";
      case 5:
        return stepTitles.step5 ?? "";
      case 6:
        return stepTitles.step6 ?? "";
      case 7:
        return stepTitles.step7 ?? "";
      case 8:
        return stepTitles.step8 ?? "";
      default:
        return "";
    }
    
  };

  const getErrorCorrectionStepButtons = (): string[] => {
    // For steps 4 and 5, only show clear if there are enabled fields
    const enabledFields = getCurrentStepFields().filter(f => !f.disabled);
    switch (currentStep) {
      case 1:
        return ["next"];
      case 2:
        return ["back", "next"];
      case 3:
        return ["back", "continue"];
      case 4:
        if (enabledFields.length === 0) return ["back", "next"];
        return ["clear", "back", "next"];
      case 5:
        if (enabledFields.length === 0) return ["back", "submit"];
        return ["clear", "back", "submit"];
      default:
        return [];
    }
  };

  const getNormalStepButtons = (): string[] => {
    if (currentStep === 1) return ["clear", "next"];
    if (currentStep === 2) return ["back", "next"];
    if (currentStep === 3) return ["clear", "back", "continue"];
    if (currentStep >= 4 && currentStep <= 6) return ["clear", "back", "next"];
    if (currentStep === 7) return ["clear", "back", "submit"];
    if (currentStep === 8) return ["close"];
    return [];
  };

  const getStepButtons = (): string[] => {
  if (isErrorCorrectionMode) return getErrorCorrectionStepButtons();
  return getNormalStepButtons();
};

  const handleNext = () => {
    if (currentStep < totalSteps) {
      // Call the onStepChange callback before changing step
      if (onStepChange) {
        onStepChange();
      }
      setCurrentStep((prev) => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      // Call the onStepChange callback before changing step
      if (onStepChange) {
        onStepChange();
      }
      setCurrentStep((prev) => prev - 1);
    }
  };
  const withResetKey = (content: React.ReactNode) => (
  <div key={resetKeys[currentStep]}>
    {content}
  </div>
);

  const handleClear = () => {
    // Clear validation errors
    if (onClearValidationErrors) {
      onClearValidationErrors();
    }

    // force re-mount
    setResetKeys((prev) => ({
      ...prev,
      [currentStep]: (prev[currentStep] || 0) + 1,
    }));

    if (!setFormValues || !initialValues) return;

    setFormValues((prev) => {
      const cleared = { ...prev };
      const fields = getCurrentStepFields();
      const activeClassCount = currentStep === 5
        ? Number((prev as any).classCount) || classCount || 1
        : 1;

      fields.forEach((field) => {
        if (field.disabled) return;

        for (let classIndex = 0; classIndex < activeClassCount; classIndex += 1) {
          const fieldName = classIndex === 0
            ? field.name
            : `${field.name}_class${classIndex}`;

          if (cleared[fieldName] !== initialValues[fieldName]) {
            // Clear only if the field is not disabled and the value has changed from the initial value
            delete (cleared as any)[fieldName];
          }
        }
      });

      // Explicitly reset specific fields regardless of their disabled state
      // But only if NOT in error correction mode (where these fields are disabled/read-only)
      if (!isErrorCorrectionMode) {
        cleared["totalEstimatedCourseFees"] = 0;
        cleared["estimatedMilesDollarValue"] = undefined;
      }

      return cleared;
    });
  };

  const handleAddClass = () => {
    setClassCount((prev) => prev + 1);
    setClassIds((prev) => [...prev, `${Date.now()}-${prev.length}`]);
    if (formValues) {
      const currentCount = Number((formValues as any).classCount) || 1;
      (formValues as any).classCount = currentCount + 1;
    }
  };

const handleDeleteClass = (classIndex: number) => {
  setClassCount((prev) => Math.max(1, prev - 1));
  setClassIds((prev) => prev.filter((_, index) => index !== classIndex));

  if (formValues && setFormValues) {
    setFormValues((prev) => {
      const cleanedValues = { ...prev };

      // Remove values for the specified class
      Object.keys(cleanedValues).forEach((key) => {
        if (key.includes(`_class${classIndex}`)) {
          delete cleanedValues[key];
        }
      });

      // Update remaining class indices
      Object.keys(cleanedValues).forEach((key) => {
        const regex = /_class(\d+)$/;
        const match = regex.exec(key);
        if (match) {
          const oldIndex = Number.parseInt(match[1], 10);
          if (oldIndex > classIndex) {
            const newKey = key.replace(`_class${oldIndex}`, `_class${oldIndex - 1}`);
            cleanedValues[newKey] = cleanedValues[key];
            delete cleanedValues[key];
          }
        }
      });

      // Update classCount to reflect the new number of classes
      const currentCount = Number(cleanedValues.classCount) || 1;
      cleanedValues.classCount = Math.max(1, currentCount - 1);

      return cleanedValues;
    });
  }

  // Clear validation errors for the deleted class immediately
  if (onUpdateValidationErrors && validationErrors) {
    const cleanedErrors: Record<string, string> = {};

    Object.keys(validationErrors).forEach((key) => {
      // Skip errors for the deleted class
      if (key.includes(`_class${classIndex}`)) {
        return;
      }

      // Re-index errors for classes after the deleted one
      const regex = /_class(\d+)$/;
      const match = regex.exec(key);
      if (match) {
        const oldIndex = Number.parseInt(match[1], 10);
        if (oldIndex > classIndex) {
          const newKey = key.replace(`_class${oldIndex}`, `_class${oldIndex - 1}`);
          cleanedErrors[newKey] = validationErrors[key];
        } else {
          cleanedErrors[key] = validationErrors[key];
        }
      } else {
        cleanedErrors[key] = validationErrors[key];
      }
    });

    onUpdateValidationErrors(cleanedErrors);
  }
};



  /* -------------------- HELPER FUNCTIONS -------------------- */
  
  // Helper: Build class data object from form values
  const buildClassData = (index: number): Record<string, any> => {
    const fields = getCurrentStepFields();
    const classData: Record<string, any> = {};
    
    fields.forEach((field) => {
      const fieldName = index === 0 ? field.name : `${field.name}_class${index}`;
      classData[field.name] = (formValues as any)?.[fieldName] ?? "";
    });
    
    return classData;
  };

  // Helper: Get reimbursement type for a class
  const getClassReimbursementType = (index: number): string => {
    const fieldName = index === 0 ? "reimbursementType" : `reimbursementType_class${index}`;
    return (formValues as any)?.[fieldName] ?? "";
  };

  // Helper: Build classes array from classIds
  const buildClassesArray = () => {
    const classesArray = classIds.map((id, i) => {
      const classData = buildClassData(i);
      const reimbursementType = getClassReimbursementType(i);
      return { id, index: i, reimbursementType, ...classData };
    });
    
    if (formValues) {
      (formValues as any).classes = classesArray;
    }
    
    return classesArray;
  };

  // Helper: Create field configs with class suffix
  const makeClassFields = (fields: FieldConfig[], index: number): FieldConfig[] => {
    return fields.map((field) => ({
      ...field,
      name: index === 0 ? field.name : `${field.name}_class${index}`,
    }));
  };

  // Helper: Filter fields by type (class details, travel, or miles)
  const getClassDetailsFields = (fields: FieldConfig[]): FieldConfig[] => {
    return fields.filter(
      (f) => !travelFieldNames.has(String(f.name)) && !milesFieldNames.has(String(f.name))
    );
  };

  const getTravelFields = (fields: FieldConfig[]): FieldConfig[] => {
    return fields.filter((f) => travelFieldNames.has(String(f.name)));
  };

  const getMilesFields = (fields: FieldConfig[]): FieldConfig[] => {
    return fields.filter((f) => milesFieldNames.has(String(f.name)));
  };

  // Helper: Render add class button
  const renderAddClassButton = () => (
    <button
      onClick={(e) => {
        e.preventDefault();
        handleAddClass();
      }}
      className="flex items-center gap-2 text-[20px] font-[350] text-(--Contrast-Blue,#0074AD) hover:underline justify-start"
      style={{ justifyContent: "flex-start" }}
      aria-label="Add additional class"
    >
      <img
        src={plusOutline}
        alt="Add"
        className="w-5 h-5 inline-block text-(--Contrast-Blue,#0074AD)"
      />
      <span className="text-[20px] font-[350] text-(--Contrast-Blue,#0074AD)">
        Add additional class
      </span>
    </button>
  );

  const renderDeleteClassButton = (index: number) => (
    <button
      onClick={(e) => {
        e.preventDefault();
        handleDeleteClass(index);
      }}
      className="flex items-center gap-2 text-[20px] font-[350] text-(--Contrast-Blue,#0074AD) hover:underline ml-auto justify-end mb-8!"
      style={{ marginLeft: "auto", justifyContent: "flex-end" }}
      aria-label={`Delete class ${index + 1}`}
    >
      <img
        src={trashSolid}
        alt="Delete"
        className="w-5 h-5 inline-block text-(--Contrast-Blue,#0074AD)"
      />
      <span className="text-[20px] font-[350] text-(--Contrast-Blue,#0074AD)">
        Delete additional class
      </span>
    </button>
  );

  // Helper: Render travel section for CEU classes
  const renderTravelSection = (index: number) => {
    const stepFields = getCurrentStepFields();
    const travelFields = makeClassFields(getTravelFields(stepFields), index);
    const milesFields = makeClassFields(getMilesFields(stepFields), index);

    return (
      <>
        <div className="space-y-6 mt-6">
          <h4 className="text-[22px] leading-7 tracking-[0.01px] text-[#0D1C3D] font-gotham">
            Travel information - continuing education units only
          </h4>
          <p className="text-base mt-4! p2 -book text-[#0D1C3D]">
            You need to submit receipts for all travel expenses. Check your
            policy for travel limits.
          </p>
          {renderForm(travelFields, "", [], undefined, undefined, undefined, currentStep)}
        </div>

        <div className="space-y-6 mt-6">
          {renderForm(milesFields, "", [], undefined, undefined, undefined, currentStep)}
        </div>
      </>
    );
  };

  // Helper: Render single class section
  const renderClassSection = (cls: any) => {

    const i = cls.index;
    const stepFields = getCurrentStepFields();
    const classDetailsFields = makeClassFields(getClassDetailsFields(stepFields), i);

    // DEBUG: Log field config names and initialValues for class 0 in error correction mode
    if (isErrorCorrectionMode && currentStep === 5 && i === 0) {
      // Print the field config names for class 0
      const fieldNames = classDetailsFields.map(f => f.name);
      // Print the initialValues/formValues for class 0 fields
      const values = fieldNames.reduce((acc, name) => {
        acc[name] = (formValues as any)?.[name];
        return acc;
      }, {} as Record<string, any>);
      // eslint-disable-next-line no-console
      console.log('[DEBUG] Class 0 field config:', fieldNames);
      // eslint-disable-next-line no-console
      console.log('[DEBUG] Class 0 initialValues:', values);
    }

    // Remove add/delete buttons in error correction mode step 5
    const showClassButtons = !(isErrorCorrectionMode && currentStep === 5);

    return (
      <div key={cls.id} className="mb-8">
        <h3 className="text-[26px] leading-8 tracking-[0.01px] text-[#0D1C3D] font-gotham mb-4">
          Class {i + 1}
        </h3>

        <p className="text-base p3 -book text-[#0D1C3D]">
          Travel is only reimbursed when incurred to attend required in-person
          courses and events taken for continuing education (such as CEU, PDU,
          CME, contact hours).
        </p>

        {/* Class Details */}
        <div className="space-y-6">
          {renderForm(classDetailsFields, "", [], undefined, undefined, undefined, currentStep)}
        </div>

  {/* Travel and Mileage */}
  {(cls.reimbursementType === "1" || cls.reimbursementType === "CEU") && renderTravelSection(i)}

        {/* Add/Delete Additional Class Buttons */}
        {showClassButtons && (
          <div className="flex desktop:flex-row mobile:flex-col w-full items-center mt-4 mb-8">
            <div className="flex desktop:flex-row mobile:flex-col w-full items-center mt-4 mb-8">
              {i === classCount - 1 ? renderAddClassButton() : <div />}
              {i > 0 && renderDeleteClassButton(i)}
            </div>
          </div>
        )}

        {i < classCount - 1 && <div className="border-t border-gray-300 mt-[31px]!" />}
      </div>
    );
  };

  /* -------------------- CLASS SECTIONS -------------------- */

  const renderClassSections = () => {
    // In error correction mode step 5, render all classes if there are no step 5 errors
    if (isErrorCorrectionMode && currentStep === 5) {
      const handleSubmit = () => {
        console.log('API called');
      };
      // Extract class indices from validationErrors keys
      const errorKeys = Object.keys(validationErrors);
      const classIndices = new Set<number>();
      errorKeys.forEach(key => {
        const match = /_class(\d+)/.exec(key);
        if (match) {
          classIndices.add(Number(match[1]));
        } else if (stepFields.step5?.some(f => f.name === key)) {
          classIndices.add(0);
        }
      });
      let sortedIndices = Array.from(classIndices).sort((a, b) => a - b);
      // If there are no class errors, render all classes
      if (sortedIndices.length === 0 && typeof classCount === 'number' && classCount > 0) {
        sortedIndices = Array.from({ length: classCount }, (_, i) => i);
      }
      const classesArray = sortedIndices.map(i => {
        const id = `class-${i}`;
        const classData = buildClassData(i);
        const reimbursementType = getClassReimbursementType(i);
        return { id, index: i, reimbursementType, ...classData };
      });
      return (
        <div className="flex flex-col w-full">
          <h2 className="text-[26px] leading-8 tracking-[0.01px] text-[#0D1C3D] font-gotham">
            Class information
          </h2>
          <p className="text-base leading-6 text-[#0D1C3D] mb-6">
            All fields are required unless marked as optional.
          </p>
          {classesArray.map((cls: any) => renderClassSection(cls))}
          {renderForm(
            [],
            "",
            getStepButtons(),
            handleSubmit,
            handleBack,
            handleClear,
            currentStep
          )}
        </div>
      );
    }
    // Normal flow
    const classesArray = buildClassesArray();
    return (
      <div className="flex flex-col w-full">
        <h2 className="text-[26px] leading-8 tracking-[0.01px] text-[#0D1C3D] font-gotham">
          Class information
        </h2>
        <p className="text-base leading-6 text-[#0D1C3D] mb-6">
          All fields are required unless marked as optional.
        </p>
        {classesArray.map((cls: any) => renderClassSection(cls))}
        {renderForm(
          [],
          "",
          getStepButtons(),
          handleNext,
          handleBack,
          handleClear,
          currentStep
        )}
      </div>
    );
  };

  const renderPrimaryApproverInfo = () => {
    if (currentStep !== 3) return null;
    
    // Get fields with proper disabled state from getCurrentStepFields
    const step3Fields = getCurrentStepFields();
    
    // Use isAltField property to separate primary and alternate fields
    const primaryFields = step3Fields.filter((f) => !f.isAltField);
    
    // For admin create, remove Alternate NUID field from alternate section
    const alternateFields = step3Fields
      .filter((f) => f.isAltField && (isAdminCreate ? f.name !== 'alternateNuid' : true))
      .map((field) => {
        // In error correction mode, all fields should already be disabled from getCurrentStepFields
        if (isErrorCorrectionMode) {
          return field; // Already disabled, don't modify
        }
        
        // Disable altPhone field only when there's no alternateNuid in form data
        if (field.name === 'altPhone') {
          // Check both alternateNuid and altNuid fields
          const alternateNuidValue = safeGet(formValues?.alternateNuid, formValues?.altNuid);
          const hasAlternateNuid = 
            typeof alternateNuidValue === 'string' && alternateNuidValue.trim() !== '' ||
            typeof alternateNuidValue === 'number' && alternateNuidValue > 0;
          // Keep the field enabled if there's existing data, otherwise disable
          const shouldDisable = !hasAlternateNuid;
          return {
            ...field,
            disabled: shouldDisable,
            helperText: shouldDisable 
              ? 'Enter an Alternate NUID first to enable this field'
              : field.helperText,
          };
        }
        return field;
      });

    return (
      <div
        key={resetKeys[currentStep]}
        className="flex flex-col gap-6 w-full max-w-[1032px] mb-8"
      >
        <h2 className="text-[26px] leading-8 tracking-[0.01px] text-[#0D1C3D] font-gotham">
          Primary approver information
        </h2>
        <p className="text-base leading-6 text-[#0D1C3D]">
          All fields are optional unless marked as required. Users must fill out
          one field in order to proceed.
        </p>
        <p className="text-base leading-6 text-[#0D1C3D]">
          The following direct manager has been identified for you from the HR
          My org hierarchy. This manager will receive all standard emails and
          notifications of employee tuition reimbursement applications submitted
          here. If known, please provide the manager's work phone. If this
          manager information is incorrect, contact your direct manager or
          supervisor.
        </p>

        {renderForm(primaryFields, "", [])}

        <p className="text-base leading-6 text-[#0D1C3D]">
          If another manager or supervisor should also receive standard emails
          for approvals and notifications of employee tuition reimbursement
          applications submitted here, you may add that manager or supervisor
          here by NUID. If known, please provide the manager's work phone.
          (Note: Alternate approver information is utilized only for Tuition
          Reimbursement purposes)
        </p>

        {renderForm(alternateFields, "", [])}

        {renderForm(
          [],
          "",
          getStepButtons(),
          handleNext,
          handleBack,
          handleClear,
          currentStep
        )}
      </div>
    );
  };

  /* -------------------- COMMENTS SECTION -------------------- */
  
  // Helper: Check if any fees were entered in Step 5 (Class Info)
  const hasAnyFeesEntered = () => {
    if (!formValues) return false;
    
    const feeFields = [
      'estimatedTuitionFee',
      'estimatedRegistrationFee', 
      'estimatedBooksFee',
      'estimatedLabFee',
      'estimatedOtherFee',
      'estimatedAirfareBaggageFee',
      'estimatedParkTollFee',
      'estimatedAutoRentalFee',
      'estimatedPublicTransportFee',
      'estimatedHotelFee',
      'estimatedMilesDollarValue'
    ];
    
    // Check for base class (Class 1) fees
    const hasBaseFees = feeFields.some(field => {
      const value = formValues[field as keyof typeof formValues];
      return value !== undefined && value !== null && value !== '' && Number(value) > 0;
    });
    
    // Check for additional classes (Class 2, 3, etc.)
    const classCount = Number(formValues.classCount) || 1;
    let hasAdditionalClassFees = false;
    
    for (let i = 1; i < classCount; i++) {
      const hasClassFees = feeFields.some(field => {
        const classFieldName = `${field}_class${i}`;
        const value = formValues[classFieldName as keyof typeof formValues];
        return value !== undefined && value !== null && value !== '' && Number(value) > 0;
      });
      if (hasClassFees) {
        hasAdditionalClassFees = true;
        break;
      }
    }
    
    return hasBaseFees || hasAdditionalClassFees;
  };
  
  // Helper: Get comment fields by type
  const getCommentFields = () => {
    const comments = stepFields.step6 || [];
    const hasFeesEntered = hasAnyFeesEntered();
    const noFeesEntered = !hasFeesEntered;
    return {
      employee: comments.filter((f) => f.name === "employeeComments"),
      employment: comments.filter((f) => f.name === "employmentJobComments"),
      noFees: noFeesEntered 
        ? comments.filter((f) => f.name === "noEstimatedFees").map((field) => ({
            ...field,
            required: false, // Not required if fees were entered
          }))
        : [],
    };
  };
  // Helper: Render comments form content
  const renderCommentsFormContent = () => {
    const { employee, employment, noFees } = getCommentFields();
    const hasFeesEntered = hasAnyFeesEntered();

    return (
      <>
        {renderForm(employee, getCurrentStepTitle(), [], undefined, undefined, undefined, currentStep)}
        <p className="p3 --book mt-6!">
          Which job at KP do you plan to advance in with the completion
          of this program?
        </p>
        {renderForm(employment, "", [], undefined, undefined, undefined, currentStep)}
        {!hasFeesEntered && commentsBanner}
        {renderForm(noFees, "", [], undefined, undefined, undefined, currentStep)}
        {renderForm(
          [],
          "",
          getStepButtons(),
          handleNext,
          handleBack,
          handleClear,
          currentStep
        )}
      </>
    );
  };

  const renderCommentsInfo = () => {
    return (
      <div className="flex flex-col w-full max-w-[1032px] mb-8">
        <h2 className="h2">Comments</h2>
        <p className="p3 -book">
          All fields are optional unless marked as required.
        </p>
        <p className="p3 -book">
          Briefly explain how this program will help develop your career at
          Kaiser Permanente.
        </p>
        <div className="w-full">
          {renderCommentsFormContent()}
        </div>
      </div>
    );
  };

  /* -------------------- MAIN RENDER -------------------- */

  // Helper: Determine which content to render based on step
  const renderStepContent = () => {
    if (isErrorCorrectionMode) {
      // Only render steps 1-5
      if (currentStep === 3) return withResetKey(renderPrimaryApproverInfo());
      if (currentStep === 5) return withResetKey(renderClassSections());
      // For steps 1, 2, 4, render as usual
      return (
        <div key={resetKeys[currentStep]}>
          {renderForm(
            getCurrentStepFields(),
            getCurrentStepTitle(),
            getStepButtons(),
            handleNext,
            handleBack,
            handleClear,
            currentStep
          )}
        </div>
      );
    }
    // Normal flow
    if (showSuccess) {
      return (
        <>
          {warningBanner}
          {successContent}
        </>
      );
    }
    // Show upload/submit error only when there are validationErrors on final step
    if (currentStep === 8 && Object.keys(validationErrors).length > 0) {
      return (
        <>
          {warningBanner}
          <UploadErrorBanner 
            header="Error"
            message="There was an issue submitting your application. Please try again."
          />
        </>
      );
    }
    if (currentStep === 3) return withResetKey(renderPrimaryApproverInfo());
    if (currentStep === 5) {
      return withResetKey(renderClassSections());
    }
    if (currentStep === 6) return withResetKey(renderCommentsInfo());

    // Custom submit handler for step 7 in admin mode + error correction mode
    const handleSubmit = () => {
      if (currentStep === 7 && mode === "admin" && isErrorCorrectionMode) {
        console.log("errors to correct api");
      } else {
        handleNext();
      }
    };

    return (
      <div key={resetKeys[currentStep]}>
        {renderForm(
          getCurrentStepFields(),
          getCurrentStepTitle(),
          getStepButtons(),
          currentStep === 7 ? handleSubmit : handleNext,
          handleBack,
          handleClear,
          currentStep
        )}
      </div>
    );
  };

  // Build field labels from current step fields
  const fieldLabels = useMemo(() => {
    const fields = getCurrentStepFields();
    return fields.reduce((acc, field) => {
      acc[field.name] = field.label;
      return acc;
    }, {} as Record<string, string>);
  }, [currentStep]);

  // Filter validation errors to only show errors for fields in the current step
  const currentStepValidationErrors = useMemo(() => {
    const currentStepFields = getCurrentStepFields();
    const currentStepFieldNames = new Set(currentStepFields.map(f => f.name));
    
    // Filter validationErrors to only include fields from current step
    const filtered = Object.keys(validationErrors).reduce((acc, fieldName) => {
      // In error correction mode, never include applicationYear errors
      if (isErrorCorrectionMode && fieldName === 'applicationYear') {
        return acc;
      }
      
      if (currentStepFieldNames.has(fieldName)) {
        acc[fieldName] = validationErrors[fieldName];
      }
      return acc;
    }, {} as Record<string, string>);
    

    
    return filtered;
  }, [validationErrors, currentStep, isErrorCorrectionMode]);

  
  // Debug: Log what is being passed to renderForm
  const currentStepFields = getCurrentStepFields();
  console.log('[FormStepperTuition] Step field names:', currentStepFields.map(f => f.name));
  return (
    <div className="grid-container max-w-7xl mx-auto px-4">
      {!hideBreadcrumbs && (
        <>
          {isAdminCreate ? (
            <BreadCrumbs
              previousPage="Workplace development"
              currentPage="Tuition reimbursement"
              onPreviousClick={() => {
                navigate('/admin');
                dispatch(clearForm());
              }}
            />
          ) : (
            <BreadCrumbs
              previousPage="Back to tuition reimbursement"
              currentPage="Apply"
              onPreviousClick={() => {
                navigate("/tuition");
                dispatch(clearForm());
              }}
            />
          )}
        </>
      )}

      {!isErrorCorrectionMode && (
        <h1 className="ds-type__h2 mt-8 mb-0">Apply</h1>
      )}

      <div className="flex desktop:flex-row gap-8">
        <div className="hidden desktop:block w-1/4">
          <StepIndicator items={items} />
        </div>

        <div className="w-full desktop:w-3/4">
        {adminEmpIdErrorBanner}
        {patchErrorBanner}
        {updateManagerDetailsErrorBanner}

          {/* Show a single error banner per step in error correction mode. If both step-specific and globalError exist, show both in one banner. */}
          {isErrorCorrectionMode && (Object.keys(currentStepValidationErrors).length > 0 || (validationErrors && Object.keys(validationErrors).some(key => key.startsWith('globalError')))) && (
            <div ref={errorBannerRef}>
              <TuitionErrorBanner
                currentStep={currentStep}
                errors={{
                  ...currentStepValidationErrors,
                  ...Object.keys(validationErrors || {})
                    .filter(key => key.startsWith('globalError'))
                    .reduce((acc, key) => {
                      acc[key] = validationErrors[key];
                      return acc;
                    }, {} as Record<string, string>)
                }}
                fieldLabels={fieldLabels}
                formData={formValues}
              />
            </div>
          )}
          {/* Display error banner for other steps as usual */}
          {!isErrorCorrectionMode && Object.keys(currentStepValidationErrors).length > 0 && (currentStep === 1 || currentStep === 3 || currentStep === 4 || currentStep === 5 || currentStep === 6) && (
             <div ref={errorBannerRef}>
            <TuitionErrorBanner
              currentStep={currentStep}
              errors={currentStepValidationErrors}
              fieldLabels={fieldLabels}
              formData={formValues}
            />
            </div>
          )}
          {renderStepContent()}
        </div>
      </div>

      {/* Scroll to Top Button */}
      <ScrollToTopBtn />
    </div>
  );
};

export default FormStepperTuition;